// @flow

/**
 * Resolve Data Displays for Export
 *
 * Pre-processes markdown before PDF/DOCX export by replacing <data-display> tags:
 * - Table displays → markdown tables (handled by existing PDF/DOCX table rendering)
 * - Graph displays → chart image tokens ({{CHART_IMAGE:displayId}}) for image embedding,
 *   with markdown table fallback if image capture fails
 *
 * Chart images are captured from the live DOM elements (already rendered on screen)
 * using html2canvas. This is far more reliable than off-screen rendering since
 * ResponsiveContainer and Recharts are already sized and painted.
 *
 * This runs BEFORE prepareMarkdownForExport, which handles <prompt> and other tags.
 */

import {
  convertTableToMarkdown,
  convertGraphToMarkdown,
} from './convertDisplayToMarkdown';

export type ChartImageMap = { [string]: string };

export type ResolvedExport = {
  markdown: string,
  chartImages: ChartImageMap,
};

/**
 * Capture a chart from the live DOM by its data-display-id attribute.
 * Returns a PNG data URL or null if the element isn't found or capture fails.
 */
async function captureChartFromDom(displayId: string): Promise<string | null> {
  try {
    const element = document.querySelector(`[data-display-id="${displayId}"]`);
    if (!element) {
      console.warn(`[resolveDataDisplaysForExport] DOM element not found for displayId: ${displayId}`);
      return null;
    }

    // Dynamic import to avoid loading html2canvas unless exporting
    const htmlToCanvasModule = await import('html2canvas');
    const html2canvas = htmlToCanvasModule.default || htmlToCanvasModule;

    const canvas = await html2canvas(element, {
      backgroundColor: '#ffffff',
      scale: 2,
      logging: false,
      useCORS: true,
    });

    return canvas.toDataURL('image/png');
  } catch (err) {
    console.error(`[resolveDataDisplaysForExport] Failed to capture chart ${displayId}:`, err);
    return null;
  }
}

/**
 * Resolve all <data-display> tags in markdown for export.
 *
 * @param {string} markdown - The message markdown text
 * @param {Object} dataDisplays - Map of displayId → display definition
 * @returns {Promise<ResolvedExport>} Resolved markdown and chart images
 */
export async function resolveDataDisplaysForExport(
  markdown: string,
  dataDisplays: ?{ [string]: Object },
): Promise<ResolvedExport> {
  if (!dataDisplays || Object.keys(dataDisplays).length === 0) {
    return { markdown, chartImages: {} };
  }

  const chartImages: ChartImageMap = {};
  let resolved = markdown;

  // Identify chart displays that need image capture
  const chartEntries = Object.entries(dataDisplays).filter(
    ([_, def]) => def.displayType === 'composedGraph',
  );

  // Capture chart images in parallel from the live DOM
  if (chartEntries.length > 0) {
    const captures = await Promise.all(
      chartEntries.map(async ([displayId]) => {
        const png = await captureChartFromDom(displayId);
        return [displayId, png];
      }),
    );
    captures.forEach(([id, png]) => {
      if (png) chartImages[id] = png;
    });
  }

  // Replace <data-display> tags in the markdown
  for (const [displayId, definition] of Object.entries(dataDisplays)) {
    const escapedId = displayId.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const tagPattern = new RegExp(
      `<data-display\\s+id=["']?${escapedId}["']?\\s*>\\s*</data-display>`,
      'g',
    );

    if (definition.displayType === 'table') {
      // Tables → native markdown tables (PDF/DOCX renderers handle these)
      resolved = resolved.replace(tagPattern, convertTableToMarkdown(definition));
    } else if (chartImages[displayId]) {
      // Chart with successful image capture → image token
      resolved = resolved.replace(
        tagPattern,
        `\n\n**${definition.title || 'Chart'}**\n\n{{CHART_IMAGE:${displayId}}}\n\n`,
      );
    } else {
      // Chart without image → fallback to markdown table representation
      resolved = resolved.replace(tagPattern, convertGraphToMarkdown(definition));
    }
  }

  return { markdown: resolved, chartImages };
}
